import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptPromise = null;

function loadTurnstileScript() {
	if (window.turnstile) {
		return Promise.resolve();
	}

	if (scriptPromise) {
		return scriptPromise;
	}

	scriptPromise = new Promise(function (resolve, reject) {
		let script = document.querySelector('script[src="' + SCRIPT_SRC + '"]');

		if (script) {
			script.addEventListener("load", function () {
				resolve();
			});

			script.addEventListener("error", function () {
				scriptPromise = null;
				reject(new Error("Failed to load Turnstile script"));
			});

			return;
		}

		script = document.createElement("script");
		script.src = SCRIPT_SRC;
		script.async = true;
		script.defer = true;

		script.onload = function () {
			resolve();
		};

		script.onerror = function () {
			scriptPromise = null;
			reject(new Error("Failed to load Turnstile script"));
		};

		document.head.appendChild(script);
	});

	return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile(
	{ siteKey, onVerify, onExpire, onError },
	ref,
) {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);

	const callbacksRef = useRef({
		onVerify: onVerify,
		onExpire: onExpire,
		onError: onError,
	});

	// Keep callbacks current without recreating the widget.
	useEffect(function () {
		callbacksRef.current = {
			onVerify: onVerify,
			onExpire: onExpire,
			onError: onError,
		};
	});

	// Allow the parent component to call ref.current.reset().
	useImperativeHandle(
		ref,
		function () {
			return {
				reset: function () {
					if (widgetIdRef.current !== null && window.turnstile) {
						window.turnstile.reset(widgetIdRef.current);
					}
				},
			};
		},
		[],
	);

	useEffect(
		function () {
			let cancelled = false;

			loadTurnstileScript()
				.then(function () {
					if (cancelled || !containerRef.current || !window.turnstile) {
						return;
					}

					widgetIdRef.current = window.turnstile.render(containerRef.current, {
						sitekey: siteKey,

						callback: function (token) {
							if (callbacksRef.current.onVerify) {
								callbacksRef.current.onVerify(token);
							}
						},

						"expired-callback": function () {
							if (callbacksRef.current.onExpire) {
								callbacksRef.current.onExpire();
							}
						},

						"error-callback": function () {
							if (callbacksRef.current.onError) {
								callbacksRef.current.onError();
							}
						},
					});
				})
				.catch(function () {
					if (callbacksRef.current.onError) {
						callbacksRef.current.onError();
					}
				});

			return function () {
				cancelled = true;

				if (widgetIdRef.current !== null && window.turnstile) {
					window.turnstile.remove(widgetIdRef.current);
					widgetIdRef.current = null;
				}
			};
		},
		[siteKey],
	);

	return <div ref={containerRef} />;
});

export default Turnstile;
